import React, { useCallback, useState } from 'react'
import { createPortal } from 'react-dom'
import { useViewportDropdown } from './hooks/useViewportDropdown'
import s from './ViewportDropdown.module.scss'

/**
 * Shared viewport-aware dropdown.
 * Renders the menu in a portal and keeps it visible inside the viewport
 * (flips above/below, shifts horizontally, scrolls internally when needed).
 */
export const ViewportDropdown = ({
	value,
	options = [],
	onChange,
	disabled = false,
	getOptionValue = option => option?.value ?? option,
	getOptionLabel = option => option?.label ?? String(option ?? ''),
	renderTrigger,
	renderOption,
	menuClassName,
	estimatedMenuHeight = 120,
	minMenuWidth = 148,
	preferredPlacement = 'bottom',
	horizontalAlign = 'start',
	menuZIndex = 2000,
	shouldCloseOnSelect,
	isOutsideClickIgnored,
}) => {
	const [isOpen, setIsOpen] = useState(false)

	const close = useCallback(() => setIsOpen(false), [])
	const toggle = useCallback(() => {
		if (disabled) return
		setIsOpen(open => !open)
	}, [disabled])

	const { triggerRef, menuRef, menuStyle, isReady } = useViewportDropdown({
		isOpen,
		onClose: close,
		estimatedMenuHeight,
		minMenuWidth,
		preferredPlacement,
		horizontalAlign,
		menuZIndex,
		isOutsideClickIgnored,
	})

	const handleSelect = useCallback(
		nextValue => {
			onChange?.(nextValue)
			const closeAfter =
				typeof shouldCloseOnSelect === 'function'
					? shouldCloseOnSelect(nextValue)
					: true
			if (closeAfter) close()
		},
		[close, onChange, shouldCloseOnSelect],
	)

	const menuClassNames = [s.menu, menuClassName].filter(Boolean).join(' ')

	const menu = isOpen
		? createPortal(
				<div
					ref={menuRef}
					className={menuClassNames}
					role="listbox"
					style={{
						position: 'fixed',
						top: menuStyle?.top,
						bottom: menuStyle?.bottom,
						left: menuStyle?.left ?? 0,
						minWidth: menuStyle?.minWidth ?? minMenuWidth,
						maxWidth: menuStyle?.maxWidth,
						maxHeight: isReady ? menuStyle?.maxHeight : undefined,
						overflowY: isReady ? menuStyle?.overflowY : 'hidden',
						zIndex: menuStyle?.zIndex ?? menuZIndex,
						visibility: isReady ? 'visible' : 'hidden',
					}}
					onWheel={event => event.stopPropagation()}
				>
					{options.map(option => {
						const optionValue = getOptionValue(option)
						const selected = optionValue === value
						if (renderOption) {
							return renderOption({
								option,
								value: optionValue,
								label: getOptionLabel(option),
								selected,
								onSelect: () => handleSelect(optionValue),
								close,
							})
						}
						return (
							<button
								key={String(optionValue)}
								type="button"
								role="option"
								aria-selected={selected}
								onClick={() => handleSelect(optionValue)}
							>
								{getOptionLabel(option)}
							</button>
						)
					})}
				</div>,
				document.body,
			)
		: null

	return (
		<>
			{renderTrigger({
				value,
				isOpen,
				disabled,
				toggle,
				triggerRef,
				placement: menuStyle?.placement,
			})}
			{menu}
		</>
	)
}

export default ViewportDropdown
